/**
 * Route helper utilities
 * 
 * Centralized helpers to identify route types and prevent routing issues
 */

/**
 * Check if a route is a superadmin route
 * @param pathname - Pathname string
 * @returns true if route starts with /superadmin
 */
export function isSuperadminPath(pathname: string): boolean {
  return pathname.startsWith('/superadmin');
}

/**
 * Check if a route is a superadmin route (legacy alias for compatibility)
 * @param location - Location object or pathname string
 * @returns true if route starts with /superadmin
 */
export function isSuperadminRoute(location: { pathname: string } | string): boolean {
  const pathname = typeof location === 'string' ? location : location.pathname;
  return isSuperadminPath(pathname);
}

/**
 * Check if a route is a public or auth route (login, register, marketing pages, etc.)
 * @param pathname - Pathname string
 * @returns true if route is public/auth route
 */
export function isPublicOrAuthPath(pathname: string): boolean {
  // Check for public proposal route pattern: /:company/p/:token
  const publicProposalPattern = /^\/[^/]+\/p\/[^/]+$/;
  if (publicProposalPattern.test(pathname)) {
    return true;
  }
  
  return (
    pathname === '/' ||
    pathname === '/login' ||
    pathname.startsWith('/superadmin/login') ||
    pathname === '/register' ||
    pathname === '/leadsregister' ||
    pathname.startsWith('/leadsregister/') ||
    pathname.startsWith('/features') ||
    pathname.startsWith('/pricing') ||
    pathname.startsWith('/about') ||
    pathname.startsWith('/contact') ||
    pathname === '/main'
  );
}

/**
 * Check if a route is a company route (requires company slug)
 * @param location - Location object or pathname string
 * @returns true if route matches /:company/... pattern (not superadmin, not public/auth)
 */
export function isCompanyRoute(location: { pathname: string } | string): boolean {
  const pathname = typeof location === 'string' ? location : location.pathname;
  
  // Exclude superadmin routes and public/auth routes
  if (isSuperadminPath(pathname) || isPublicOrAuthPath(pathname)) {
    return false;
  }
  
  // Company routes have format /:company/...
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return false;
  
  const first = parts[0];
  // Anything that isn't 'superadmin' and isn't an explicit public/auth slug
  return !!first && first !== 'superadmin' && first !== 'undefined';
}

/**
 * Get company slug from route if it's a company route
 * @param location - Location object or pathname string
 * @returns company slug or null if not a company route
 */
export function getCompanySlugFromRoute(location: { pathname: string } | string): string | null {
  if (isSuperadminRoute(location)) {
    return null;
  }
  const pathname = typeof location === 'string' ? location : location.pathname;
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length >= 1 && parts[0] !== 'superadmin' && parts[0] !== 'undefined') {
    return parts[0];
  }
  return null;
}

/**
 * Check if a role is superadmin
 * @param role - User role string
 * @returns true if role is superadmin
 */
export function isSuperadminRole(role?: string | null): boolean {
  return role === 'superadmin';
}

